import React, { useState, useEffect, useRef, useCallback } from "react";
import { SendHorizontal, X, AlertTriangle, MessageSquarePlus } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./ChatWidget.css";
import botIcon from "../../assets/iconChat.png";
import Welcome from "../ChatWidget/Welcome";
import ConsentScreen from "../ChatWidget/ConsentScreen";
import HeaderChat from "../ChatWidget/HeaderChat";
import {useChat} from "../../hooks/useChat";
import {storage} from "../../storage/storage";

function hexToRgb(hex: string): string | null {
    const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return null;
    return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

/* ---- Smooth word-by-word streaming (no buffering) ---- */
function StreamingText({ text, isActive }: { text: string; isActive: boolean }) {
    const prevLenRef = useRef(0);
    const words = text.split(/(\s+)/);
    const threshold = prevLenRef.current;

    useEffect(() => {
        prevLenRef.current = words.length;
    });

    useEffect(() => {
        if (!isActive) prevLenRef.current = 0;
    }, [isActive]);

    if (!isActive) {
        return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
    }

    return (
        <span>
            {words.map((segment, i) => (
                <span key={i} className={i >= threshold ? "stream-word-new" : undefined}>
                    {segment}
                </span>
            ))}
        </span>
    );
}

const ChatWidget = () => {
    const {
        messages,
        isLoading,
        isInitializing,
        clientInvalid,
        apiKeyInvalid,
        originNotAllowed,
        sessionErrorMessage,
        status,
        statusText,
        limitReached,
        limitWarning,
        messageCount,
        MESSAGE_LIMIT,
        clientDisplayName,
        clientLayoutColor,
        isChatOpen,
        isWidgetOpen,
        sendMessage,
        startNewChat,
        openWidget,
        closeWidget,
        openChat,
        closeChat,
    } = useChat();

    const brandName = clientDisplayName || "RMT-AI";

    const [inputMessage, setInputMessage] = useState("");
    const [hasConsented, setHasConsented] = useState(() => storage.get<boolean>("rmt-chat-consent", false));
    const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';

    // Notify parent iframe of widget state changes so it can resize
    useEffect(() => {
        if (!isEmbed) return;
        const state = isChatOpen ? 'chat' : isWidgetOpen ? 'widget' : 'closed';
        window.parent.postMessage({ type: 'rmt-chat-state', state }, '*');
    }, [isWidgetOpen, isChatOpen, isEmbed]);

    // Send initial embed state on mount (match actual widget state)
    useEffect(() => {
        if (!isEmbed) return;
        const state = isChatOpen ? 'chat' : isWidgetOpen ? 'widget' : 'closed';
        window.parent.postMessage({ type: 'rmt-chat-state', state }, '*');
    }, [isEmbed]);

    const lastMessageRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Smooth scroll to bottom whenever messages change or during streaming
    const scrollToBottom = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading, isChatOpen, scrollToBottom]);

    // Auto-focus input on desktop when chat opens or bot finishes responding
    useEffect(() => {
        if (!isChatOpen) return;
        if (isLoading) return;
        const isDesktop = !window.matchMedia('(max-width: 480px)').matches;
        if (isDesktop) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isChatOpen, isLoading]);

    // Mobile keyboard fix: lock body scroll & resize chat to visual viewport
    useEffect(() => {
        if (!isChatOpen) return;
        const isMobile = window.matchMedia('(max-width: 480px)').matches;
        if (!isMobile) return;

        // Lock body scroll to prevent iOS from pushing fixed elements
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';

        // Use visualViewport to resize chat-modal when keyboard opens/closes
        const vv = window.visualViewport;
        const chatModal = document.querySelector('.chat-modal') as HTMLElement | null;

        const handleViewportResize = () => {
            if (!chatModal || !vv) return;
            chatModal.style.height = `${vv.height}px`;
            chatModal.style.top = `${vv.offsetTop}px`;
        };

        if (vv) {
            vv.addEventListener('resize', handleViewportResize);
            vv.addEventListener('scroll', handleViewportResize);
        }

        return () => {
            // Restore body scroll
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            window.scrollTo(0, scrollY);

            // Clean up viewport listeners
            if (vv) {
                vv.removeEventListener('resize', handleViewportResize);
                vv.removeEventListener('scroll', handleViewportResize);
            }
            if (chatModal) {
                chatModal.style.height = '';
                chatModal.style.top = '';
            }
        };
    }, [isChatOpen]);

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;
        await sendMessage(inputMessage);
        setInputMessage("");
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleOpenFAQ = () => {
        closeWidget();
        const faqEl = document.getElementById("faq");
        if (faqEl) {
            faqEl.scrollIntoView({ behavior: "smooth" });
        } else {
            window.location.hash = "faq";
        }
    };

    // In embed mode, pre-resize the iframe BEFORE React renders the modal
    // to prevent the modal from briefly rendering in the tiny closed iframe
    const embedPreResize = (targetState: string, callback: () => void) => {
        if (isEmbed) {
            window.parent.postMessage({ type: 'rmt-chat-state', state: targetState }, '*');
            setTimeout(callback, 50);
        } else {
            callback();
        }
    };

    const handleToggleClick = () => {
        if (isChatOpen) {
            closeChat();
            closeWidget();
        } else if (isWidgetOpen) {
            closeWidget();
        } else {
            const targetState = messages.length > 0 ? 'chat' : 'widget';
            embedPreResize(targetState, openWidget);
        }
    };

    const handleAcceptConsent = () => {
        storage.set("rmt-chat-consent", true);
        setHasConsented(true);
    };

    const handleOpenChat = () => {
        embedPreResize('chat', () => { openChat(); });
    };

    // @ts-ignore
    return (
        <div
            className={`rmt-chat-widget ${isEmbed ? 'rmt-embed-mode' : ''}`}
            style={clientLayoutColor ? { '--primary-color': clientLayoutColor, '--secondary-color': clientLayoutColor, '--primary-rgb': hexToRgb(clientLayoutColor) || '254, 97, 27' } as React.CSSProperties : undefined}
        >
            <div className={`widget-toggle-wrap ${isWidgetOpen || isChatOpen ? 'is-open' : ''} ${isChatOpen ? 'hide-on-mobile' : ''}`}>
                {!isWidgetOpen && !isChatOpen && (
                    <>
                        <span className="orbit-ring"></span>
                        <span className="orbit-dot dot-1"></span>
                        <span className="orbit-dot dot-2"></span>
                        <span className="orbit-dot dot-3"></span>
                        <span className="pulse-ring"></span>
                    </>
                )}
                <button
                    className={`widget-toggle-btn ${isWidgetOpen || isChatOpen ? 'is-open' : ''}`}
                    onClick={handleToggleClick}
                    aria-label="Open support chat"
                >
                    {isWidgetOpen || isChatOpen ? (
                        <X size={24} color="#fff" />
                    ) : (
                        <img src={botIcon} style={{ width: 40 }} alt="Open Support Chat" />
                    )}
                </button>
            </div>

            {isWidgetOpen && !isChatOpen && (
                <div className="widget-modal">
                    <div className="widget-body">
                        {isInitializing ? (
                            <div className="welcome skeleton-welcome">
                                <div className="skeleton-circle" />
                                <div className="skeleton-text-group">
                                    <div className="skeleton-line skeleton-line--title" />
                                    <div className="skeleton-line skeleton-line--subtitle" />
                                </div>
                                <div className="welcome-actions" style={{ padding: '0 8px' }}>
                                    <div className="skeleton-btn" />
                                    <div className="skeleton-btn" />
                                </div>
                            </div>
                        ) : clientInvalid ? (
                            <div className="welcome welcome-error">
                                <div className="welcome-error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="12"/>
                                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div className="title">Configuration Error</div>
                                    <div className="subtitle">
                                        Invalid or missing client ID. Please check your integration settings.
                                    </div>
                                </div>
                            </div>
                        ) : originNotAllowed ? (
                            <div className="welcome welcome-error">
                                <div className="welcome-error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                                    </svg>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div className="title">Origin Not Allowed</div>
                                    <div className="subtitle">
                                        {sessionErrorMessage || "This domain is not authorized to use this widget. Please contact the administrator."}
                                    </div>
                                </div>
                            </div>
                        ) : apiKeyInvalid ? (
                            <div className="welcome welcome-error">
                                <div className="welcome-error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                    </svg>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div className="title">Authentication Error</div>
                                    <div className="subtitle">
                                        {sessionErrorMessage || "Invalid or missing API key. Please check your widget configuration."}
                                    </div>
                                </div>
                            </div>
                        ) : !hasConsented ? (
                            <ConsentScreen onAccept={handleAcceptConsent} />
                        ) : (
                            <Welcome openChat={handleOpenChat} brandName={brandName} />
                        )}
                    </div>
                </div>
            )}

            {isChatOpen && (
                <div className="chat-modal">
                    <HeaderChat closeChat={closeChat} closeWidget={closeWidget} startNewChat={startNewChat} brandName={brandName} />

                    <div className="chat-messages" ref={messagesContainerRef}>
                        {isInitializing ? (
                            <div className="skeleton-chat">
                                <div className="skeleton-msg skeleton-msg--bot">
                                    <div className="skeleton-avatar" />
                                    <div className="skeleton-bubble skeleton-bubble--long" />
                                </div>
                                <div className="skeleton-msg skeleton-msg--user">
                                    <div className="skeleton-bubble skeleton-bubble--short" />
                                </div>
                                <div className="skeleton-msg skeleton-msg--bot">
                                    <div className="skeleton-avatar" />
                                    <div className="skeleton-bubble skeleton-bubble--medium" />
                                </div>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="empty-state">
                                <div className="welcome-message">
                                    <div className="welcome-emoji">
                                        <img src={botIcon} style={{ width: 56 }} alt="RMT-AI" />
                                    </div>
                                    <h5>Welcome to {brandName}</h5>
                                    <p>Ask me anything, I'm here to help.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {messages?.map((message, index) => {
                                    const lastUserIndex = messages.reduce((acc: number, m, i) => m.sender === 'user' ? i : acc, -1);
                                    const isScrollTarget = index === lastUserIndex;
                                    const isLastBot = message.sender === "bot" && index === messages.length - 1;
                                    const isStreaming = isLastBot && status === "typing";
                                    return (
                                        <div key={message.id} ref={isScrollTarget ? lastMessageRef : undefined} className={`message ${message.sender}${isStreaming ? ' streaming-message' : ''}`}>
                                            <div className="message-row">
                                                {message.sender === "bot" && (
                                                    <div className={`message-avatar${isStreaming ? ' thinking-avatar' : ''}`}>
                                                        <img src={botIcon} alt="RMT-AI" />
                                                        {isStreaming && (
                                                            <>
                                                                <span className="avatar-orbit-ring"></span>
                                                                <span className="avatar-orbit-dot avatar-dot-1"></span>
                                                                <span className="avatar-orbit-dot avatar-dot-2"></span>
                                                                <span className="avatar-orbit-dot avatar-dot-3"></span>
                                                                <span className="avatar-pulse-ring"></span>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="message-content">
                                                    <div className={`message-text${isStreaming ? ' streaming' : ''}`}>
                                                        {isStreaming ? (
                                                            <StreamingText text={message.text} isActive={true} />
                                                        ) : (
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {message.text}
                                                            </ReactMarkdown>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {status === "thinking" && (
                                    <div className="message bot streaming-message">
                                        <div className="message-row">
                                            <div className="message-avatar thinking-avatar">
                                                <img src={botIcon} alt="RMT-AI" />
                                                <span className="avatar-orbit-ring"></span>
                                                <span className="avatar-orbit-dot avatar-dot-1"></span>
                                                <span className="avatar-orbit-dot avatar-dot-2"></span>
                                                <span className="avatar-orbit-dot avatar-dot-3"></span>
                                                <span className="avatar-pulse-ring"></span>
                                            </div>
                                            <div className="message-content">
                                                {statusText && (
                                                    <div className="typing-label">{statusText}</div>
                                                )}
                                                <div className="message-skeleton">
                                                    <div className="skeleton-line-shimmer skeleton-line--long"></div>
                                                    <div className="skeleton-line-shimmer skeleton-line--medium"></div>
                                                    <div className="skeleton-line-shimmer skeleton-line--short"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                    </div>

                    {limitWarning && !limitReached && (
                        <div className="chat-limit-warning">
                            <AlertTriangle size={14} />
                            <span>You're approaching the message limit ({messageCount}/{MESSAGE_LIMIT})</span>
                        </div>
                    )}

                    {limitReached ? (
                        <div className="chat-limit-banner">
                            <div className="chat-limit-banner-content">
                                <AlertTriangle size={18} />
                                <p>This conversation has reached its {MESSAGE_LIMIT}-message limit.</p>
                            </div>
                            <button className="btn-limit-new-chat" onClick={startNewChat}>
                                <MessageSquarePlus size={16} />
                                Start New Chat
                            </button>
                        </div>
                    ) : (
                        <div className="chat-input-area">
                            <div className="input-wrapper">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="chat-input"
                                    placeholder="Type a message..."
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isLoading}
                                />
                                <button
                                    className={`btn-send ${inputMessage.trim() ? 'active' : ''}`}
                                    onClick={handleSendMessage}
                                    disabled={isLoading || !inputMessage.trim()}
                                >
                                    <SendHorizontal size={16} />
                                </button>
                            </div>
                            <div className="input-footer">
                                {messageCount > 0 && (
                                    <span className="msg-counter">{messageCount}/{MESSAGE_LIMIT}</span>
                                )}
                                <span>Powered by <strong>{brandName}</strong></span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatWidget;
